import { DateTime } from 'luxon';
import { Constants } from './constants';

/**
 * Helpers para períodos de cierre de balance. Formato interno: 'YYYY-MM'.
 */
export class BalancePeriodUtils {

  static readonly PERIOD_FORMAT = 'yyyy-MM';

  /**
   * Acepta Date, ISO string, epoch (segundos o milisegundos — el job devuelve `createdAt` en
   * segundos con decimales) o el array de LocalDateTime de Java: [año, mes, día, hh, mm, ss].
   */
  static toDateTime(value: Date | string | number | number[] | null | undefined): DateTime | null {
    if (value === null || value === undefined || value === '') return null;
    let dt: DateTime;
    if (value instanceof Date) {
      dt = DateTime.fromJSDate(value);
    } else if (Array.isArray(value)) {
      const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0] = value;
      dt = DateTime.fromObject({ year, month, day, hour, minute, second });
    } else if (typeof value === 'number') {
      dt = DateTime.fromMillis(value < 1e12 ? Math.round(value * 1000) : value);
    } else {
      dt = DateTime.fromISO(String(value));
    }
    return dt.isValid ? dt : null;
  }

  /**
   * PROVISORIO: estima el cierre como `createdDate − N meses` (default 6) mientras el backend
   * no devuelva el período. Si no hay fecha, se toma "hoy".
   */
  static estimateClosingPeriod(
    createdDate: Date | string | number | null | undefined,
    monthsBefore: number = Constants.BALANCE_ESTIMATED_CLOSING_OFFSET_MONTHS,
    now: DateTime = DateTime.now()
  ): string {
    const base = BalancePeriodUtils.toDateTime(createdDate) ?? now;
    return base.startOf('month').minus({ months: monthsBefore }).toFormat(BalancePeriodUtils.PERIOD_FORMAT);
  }

  /**
   * Acepta 'YYYY-MM', 'YYYY-MM-DD[...]', 'MM/YYYY' o el array de Java [año, mes, día, ...]
   * (p. ej. `fechaCierre: [2026, 3, 31, 23, 59, 59]`) y devuelve 'YYYY-MM' (o null si es inválido).
   */
  static normalize(value: string | number[] | null | undefined): string | null {
    if (!value) return null;
    if (Array.isArray(value)) {
      const dt = BalancePeriodUtils.toDateTime(value);
      return dt ? dt.toFormat(BalancePeriodUtils.PERIOD_FORMAT) : null;
    }
    const v = String(value).trim();
    let dt: DateTime;
    const mmYyyy = /^(\d{1,2})\/(\d{4})$/.exec(v);
    if (mmYyyy) {
      dt = DateTime.fromObject({ year: +mmYyyy[2], month: +mmYyyy[1], day: 1 });
    } else {
      dt = DateTime.fromISO(v.length === 7 ? `${v}-01` : v);
    }
    return dt.isValid ? dt.toFormat(BalancePeriodUtils.PERIOD_FORMAT) : null;
  }

  static toDisplay(period: string | null | undefined): string {
    const normalized = BalancePeriodUtils.normalize(period);
    if (!normalized) return '';
    const [year, month] = normalized.split('-');
    return `${month}/${year}`;
  }

  static fromParts(year: number, month: number): string {
    return DateTime.fromObject({ year, month, day: 1 }).toFormat(BalancePeriodUtils.PERIOD_FORMAT);
  }

  /** Último período que puede ser cierre de balance: el mes anterior al actual. */
  static lastClosablePeriod(now: DateTime = DateTime.now()): string {
    return now.startOf('month').minus({ months: 1 }).toFormat(BalancePeriodUtils.PERIOD_FORMAT);
  }

  /** Primer período aceptado según la vigencia configurada (CA5b / CA15). */
  static oldestValidPeriod(
    validityMonths: number = Constants.BALANCE_VALIDITY_MONTHS,
    now: DateTime = DateTime.now()
  ): string {
    return now.startOf('month').minus({ months: validityMonths }).toFormat(BalancePeriodUtils.PERIOD_FORMAT);
  }

  /** true si el período no es futuro/actual y está dentro de la vigencia. */
  static isValid(
    period: string | null | undefined,
    validityMonths: number = Constants.BALANCE_VALIDITY_MONTHS,
    now: DateTime = DateTime.now()
  ): boolean {
    const normalized = BalancePeriodUtils.normalize(period);
    if (!normalized) return false;
    // Comparación lexicográfica válida para 'YYYY-MM'.
    return normalized >= BalancePeriodUtils.oldestValidPeriod(validityMonths, now)
      && normalized <= BalancePeriodUtils.lastClosablePeriod(now);
  }

  static isExpired(
    period: string | null | undefined,
    validityMonths: number = Constants.BALANCE_VALIDITY_MONTHS,
    now: DateTime = DateTime.now()
  ): boolean {
    const normalized = BalancePeriodUtils.normalize(period);
    return !!normalized && normalized < BalancePeriodUtils.oldestValidPeriod(validityMonths, now);
  }

  /** Período siguiente ('YYYY-MM' → 'YYYY-MM'). */
  static nextPeriod(period: string): string {
    const normalized = BalancePeriodUtils.normalize(period);
    return DateTime.fromISO(`${normalized}-01`).plus({ months: 1 }).toFormat(BalancePeriodUtils.PERIOD_FORMAT);
  }

  /** Períodos desde `from` hasta `to` inclusive, en orden cronológico. */
  static periodsBetween(from: string, to: string): string[] {
    const start = BalancePeriodUtils.normalize(from);
    const end = BalancePeriodUtils.normalize(to);
    if (!start || !end || start > end) return [];
    const periods: string[] = [];
    let current = start;
    while (current <= end && periods.length < 600) {
      periods.push(current);
      current = BalancePeriodUtils.nextPeriod(current);
    }
    return periods;
  }

  /**
   * Cierre "ficticio" para quien no presenta balance (persona física): el mes anterior a los
   * últimos `months` meses cerrados, así `salesRange` devuelve exactamente esos `months` meses.
   * Ej.: hoy 10/2026 y months = 6 → '2026-03' → ventas 04/2026 … 09/2026.
   */
  static closingForLastMonths(months: number, now: DateTime = DateTime.now()): string {
    return now.startOf('month').minus({ months: months + 1 }).toFormat(BalancePeriodUtils.PERIOD_FORMAT);
  }

  /**
   * CA25: rango de ventas a declarar para un cierre de balance: desde el mes siguiente al cierre
   * hasta el mes anterior al actual (el mes en curso no está disponible).
   * Ej.: cierre 12/2025 y hoy 09/2026 → 01/2026 … 08/2026.
   */
  static salesRange(closingPeriod: string | null | undefined, now: DateTime = DateTime.now()): string[] {
    const closing = BalancePeriodUtils.normalize(closingPeriod);
    if (!closing) return [];
    return BalancePeriodUtils.periodsBetween(BalancePeriodUtils.nextPeriod(closing), BalancePeriodUtils.lastClosablePeriod(now));
  }

  /**
   * 'YYYY-MM' → 'YYYY-MM-DD' con el último día del mes (un balance cierra a fin de mes).
   * Ej.: '2026-06' → '2026-06-30'.
   */
  static toClosingDate(period: string | null | undefined): string | null {
    const normalized = BalancePeriodUtils.normalize(period);
    if (!normalized) return null;
    return DateTime.fromISO(`${normalized}-01`).endOf('month').toFormat('yyyy-MM-dd');
  }
}
